<script setup>
defineOptions({ name: "SupportHero" });
import { computed } from "vue";
import { ChatBubbleLeftEllipsisIcon, ClockIcon } from "@heroicons/vue/24/outline";

const props = defineProps({
  image: { type: String, required: true },
});

// Mesmo esquema de fundo do SegmentHero: gradiente à esquerda (legibilidade do
// texto) + imagem cobrindo o restante da seção.
const sectionStyle = computed(() => ({
  backgroundImage:
    "linear-gradient(to right, rgba(15,23,42,0.88) 0%, rgba(15,23,42,0.74) 35%, rgba(15,23,42,0.35) 60%, rgba(15,23,42,0) 82%), url(" +
    props.image +
    ")",
}));

const features = [
  { type: "headset", lines: ["Atendimento", "rápido"] },
  { type: "chat", lines: ["Respostas", "claras"] },
  { type: "check", lines: ["Soluções que geram", "resultado"] },
];

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};
</script>

<template>
  <section class="overflow-hidden relative text-white bg-top bg-cover hero-section bg-slate-900"
    :style="sectionStyle">
    <div class="flex relative z-10 items-center h-full">
      <div class="relative px-4 mx-auto w-full max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal
          class="hidden absolute top-11 left-[52%] z-10 flex-col gap-2 p-3 max-w-[220px] bg-white rounded-2xl shadow-xl lg:flex">
          <div class="flex gap-2 items-start">
            <ClockIcon class="flex-shrink-0 w-8 h-8 text-[#006086]" />
            <p class="text-sm font-bold leading-snug text-gray-900">
              Sempre disponíveis quando você precisa.
            </p>
          </div>
          <span class="inline-flex self-start px-3 py-1 text-xs font-semibold text-[#006086] bg-[#e6f0f4] rounded-full">
            Seu sucesso é nossa prioridade.
          </span>
        </div>

        <div v-reveal class="max-w-2xl">
          <h1
            class="text-4xl sm:text-5xl md:text-6xl font-black tracking-normal leading-[1.15] md:leading-[1.2] text-shadow-hero">
            Suporte sempre de prontidão.
          </h1>
          <p
            class="mt-12 text-lg leading-relaxed delay-200 sm:text-xl text-white/90 animate-fadeIn text-shadow-hero-sm">
            Aqui, você fala com quem resolve.<br class="hidden sm:block" />
            Ágil, humano e eficiente.
          </p>

          <div class="grid grid-cols-1 gap-4 mt-12 sm:grid-cols-3">
            <div v-for="f in features" :key="f.lines.join(' ')" class="flex gap-3 items-center">
              <span class="flex flex-shrink-0 justify-center items-center w-11 h-11 rounded-full bg-[#e6f0f4]">
                <svg v-if="f.type === 'headset'" class="w-8 h-8 text-[#006086]" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 13v-1a9 9 0 0118 0v1" />
                  <rect x="2.5" y="13" width="4.5" height="6.5" rx="1.5" />
                  <rect x="17" y="13" width="4.5" height="6.5" rx="1.5" />
                </svg>
                <ChatBubbleLeftEllipsisIcon v-else-if="f.type === 'chat'" class="w-8 h-8 text-[#006086]" />
                <svg v-else class="w-8 h-8 text-[#006086]" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3.75" y="3.75" width="16.5" height="16.5" rx="4" />
                  <path d="M8 12.5l2.5 2.5 5-5" />
                </svg>
              </span>
              <span class="text-sm font-bold leading-snug text-white text-shadow-hero">
                <span class="block">{{ f.lines[0] }}</span>
                <span class="block">{{ f.lines[1] }}</span>
              </span>
            </div>
          </div>

          <div class="inline-flex gap-3 items-center px-5 py-3 mt-12 bg-white rounded-full shadow-md">
            <span class="flex-shrink-0 w-2.5 h-2.5 bg-green-500 rounded-full"></span>
            <span class="text-sm font-semibold text-gray-900">
              Estamos online e prontos para ajudar você.
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.hero-section {
  width: 100%;
  height: 100vh;
  height: 100dvh;
}

.text-shadow-hero {
  text-shadow:
    0 2px 10px rgba(0, 0, 0, 0.65),
    0 1px 2px rgba(0, 0, 0, 0.55);
}

.text-shadow-hero-sm {
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.55);
}

.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
